import React, { memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { housesSelectors } from '../../api/houses';
import Subtitle from '../../shared/ui/Subtitle';
import ColListWrapper from '../../shared/ui/ColListWrapper';
import FetchDataWrapper from '../../../../components/FetchDataWrapper';
import BaseCheckbox from '../../shared/ui/BaseCheckbox';
import Item from '../../shared/ui/Item';
import NotFound from '../../shared/ui/NotFound';
import { ReactComponent as HotelIcon } from '../../../../resource/HotelIcon.svg';
import { useFilteredData, filterTypes } from '../../shared/packages/filter';
import {
  houseByRegion,
  houseBySearch,
} from '../../shared/configs/filterOptions';
import {
  selectors,
  actionCreators,
  sliceKeys,
  housesFilterNames,
} from '../../model';
import {
  createHouseNumber,
  createRoomLabelByCount,
  getHouseType,
} from '../../shared/packages/address';
import { useHousesData } from '../../services/queries/houses';
import Header from './Header';

const { selectSearch, selectFilter, createHasHouseSelector } =
  selectors[sliceKeys.houses];

const { houseClicked } = actionCreators[sliceKeys.houses];

const HousesList = ({ children }) => {
  const { data, isFetching, refetch } = useHousesData();

  return (
    <FetchDataWrapper
      data={data}
      isFetching={isFetching}
      refetch={refetch}
      errMsg="Не удалось получить список домов"
    >
      <div className="colContentWrap">
        <Content data={data}>{children}</Content>
      </div>
    </FetchDataWrapper>
  );
};

export default HousesList;

const filterByParamsOptions = { [housesFilterNames.regions]: houseByRegion };

const MemoizedItem = memo(FirstLevelItem);

function Content({ data, children }) {
  const search = useSelector(selectSearch);
  const filteredDataBySearch = useFilteredData({
    data,
    value: search,
    filterType: filterTypes.bySearch,
    options: houseBySearch,
  });

  const filter = useSelector(selectFilter);
  const filteredData = useFilteredData({
    data: filteredDataBySearch,
    value: filter,
    filterType: filterTypes.byParams,
    options: filterByParamsOptions,
  });

  const items = useMemo(
    () => housesSelectors.groupByStreet(filteredData),
    [filteredData]
  );

  return (
    <>
      {children}
      {filteredData.length ? (
        <ColListWrapper grouped scrollable>
          <Header />
          {items.map(([key, data]) => (
            <MemoizedItem key={key} data={data} />
          ))}
        </ColListWrapper>
      ) : (
        <NotFound />
      )}
    </>
  );
}

function FirstLevelItem({ data }) {
  const { name, houses } = data;

  return (
    <li>
      <Subtitle stickyToTop={{ topOffset: '21px' }}>{name}</Subtitle>
      <ColListWrapper>
        {houses.map((house) => (
          <SecondLevelItem key={house.id} house={house} streetName={name} />
        ))}
      </ColListWrapper>
    </li>
  );
}

function SecondLevelItem({ house }) {
  const houseId = house.id;

  const selectHasHouse = useMemo(
    () => createHasHouseSelector(houseId),
    [houseId]
  );
  const hasHouse = useSelector(selectHasHouse);

  const dispatch = useDispatch();

  const houseNumber = createHouseNumber(house);

  const handleClick = () => {
    dispatch(houseClicked(house, !hasHouse));
  };

  return (
    <Item
      title={houseNumber}
      note={getHouseType(house)}
      description={createRoomLabelByCount(house.acceptanceAddressCount)}
      iconComponent={HotelIcon}
      action={<BaseCheckbox checked={hasHouse} />}
      onClick={handleClick}
    />
  );
}
